import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, FlaskConical, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, ts, fmtNum } from "@/lib/api";
import { EdiTag } from "@/components/EdiTag";
import { PageHeader, Panel, Th, Td, Empty, btn } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { EdiImportDialog } from "@/components/EdiImportDialog";

const Kpi = ({ label, value, to, tone = "text-slate-100", testId }) => (
  <Link to={to} data-testid={testId} className="group rounded-sm border border-slate-800 bg-[#252A35] p-5 transition-colors hover:border-slate-600">
    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`mt-2 font-heading text-3xl font-semibold ${tone}`}>{value ?? "—"}</div>
  </Link>
);

const FLOW = [
  ["SAP order", "Amazon PO keyed in SAP / Coupa / manual"],
  ["Auto-check", "Items, site, SCAC & stock"],
  ["940 out", "Warehouse Shipping Order, sent automatically"],
  ["997 in", "Group O acknowledges"],
  ["945 in", "Warehouse Shipping Advice"],
];

const Flow = () => (
  <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-slate-800 bg-slate-800 sm:grid-cols-5" data-testid="process-flow">
    {FLOW.map(([k, v], i) => (
      <div key={k} className="bg-[#1F232C] p-4">
        <div className="font-mono text-[10px] text-slate-500">STEP {i + 1}</div>
        <div className="mt-1 font-heading text-sm font-semibold text-emerald-300">{k}</div>
        <div className="text-xs text-slate-400">{v}</div>
      </div>
    ))}
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [imp, setImp] = useState(false);
  const load = () => api.get("/dashboard").then((r) => setD(r.data));
  useEffect(() => {
    load();
  }, []);
  const c = d?.customer || {};
  const r = d?.replenishment || {};

  return (
    <div className="space-y-6">
      <PageHeader title="Operations dashboard" subtitle="Live view of orders, EDI traffic and Group O xDock stock.">
        <Button onClick={() => setImp(true)} className={btn.primary} data-testid="dashboard-import-edi-btn">
          <Upload className="mr-1.5 h-4 w-4" /> Import EDI
        </Button>
      </PageHeader>
      {d?.test_mode && (
        <Link to="/settings" data-testid="dashboard-test-mode-banner" className="flex items-center gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          <FlaskConical className="h-4 w-4" /> Test mode is ON — outputs are generated and held for review, nothing is transmitted to Group O.
        </Link>
      )}
      <Flow />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 xl:grid-cols-8">
        <Kpi label="New customer orders" value={c.received} to="/orders" testId="kpi-new-orders" />
        <Kpi label="Orders on hold" value={d?.on_hold} to="/orders" tone="text-amber-300" testId="kpi-on-hold" />
        <Kpi label="Outputs to review" value={d?.to_review} to="/outputs" tone="text-amber-300" testId="kpi-to-review" />
        <Kpi label="940 released" value={c.released} to="/orders" tone="text-sky-300" testId="kpi-released" />
        <Kpi label="Short shipped" value={c.short_shipped} to="/orders" tone="text-rose-300" testId="kpi-short-shipped" />
        <Kpi label="Replen. in transit" value={r.in_transit} to="/replenishment" tone="text-sky-300" testId="kpi-in-transit" />
        <Kpi label="EDI exceptions" value={d?.exceptions} to="/edi" tone="text-rose-300" testId="kpi-exceptions" />
        <Kpi label="997 overdue" value={d?.ack_overdue?.length} to="/edi" tone="text-rose-300" testId="kpi-ack-overdue" />
      </div>
      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <Panel title="Recent EDI traffic" action={<Link to="/edi" className="text-xs text-sky-300 hover:underline" data-testid="dashboard-view-edi-log">View log</Link>} testId="dashboard-recent-edi">
          {d?.recent?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <tbody className="divide-y divide-slate-800/70">
                  {d.recent.map((m) => (
                    <tr key={m.id} className="hover:bg-slate-800/30">
                      <Td className="whitespace-nowrap font-mono text-amber-300"><EdiTag code={m.doc_type} prefix={m.direction === "out" ? "→ " : "← "} /></Td>
                      <Td className="text-slate-200">{m.summary}</Td>
                      <Td><StatusBadge status={m.status} /></Td>
                      <Td className="whitespace-nowrap font-mono text-slate-500">{ts(m.created_at)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty testId="dashboard-recent-empty">No EDI yet — import an Amazon order (SAP, Coupa or manual) or a Group O 850 to start.</Empty>
          )}
        </Panel>
        <Panel title={`Low cover · below ${d?.threshold_days ?? "—"} days${d?.peak ? " (peak)" : ""}`} testId="dashboard-low-cover">
          {d?.low_cover?.length ? (
            <table className="w-full">
              <thead><tr><Th>Part</Th><Th>xDock qty</Th><Th>Days</Th></tr></thead>
              <tbody className="divide-y divide-slate-800/70">
                {d.low_cover.map((i) => (
                  <tr key={i.id}>
                    <Td className="font-mono text-slate-100"><AlertTriangle className="mr-1 inline h-3 w-3 text-rose-400" />{i.part_no}</Td>
                    <Td>{fmtNum(i.xdock_on_hand)}</Td>
                    <Td className="text-rose-300">{i.days_cover}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty testId="dashboard-low-cover-empty">
              No alerts. Set weekly forecasts in the <Link to="/items" className="text-sky-300 hover:underline">item master</Link> to track days of cover.
            </Empty>
          )}
          <div className="border-t border-slate-800 px-5 py-3">
            <Link to="/inventory" className="inline-flex items-center gap-1 text-xs text-sky-300 hover:underline" data-testid="dashboard-view-inventory">
              Inventory by site <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </Panel>
      </div>
      <Panel title={`Awaiting 997 from Group O · over ${d?.ack_overdue_hours ?? 24}h`} action={<Link to="/edi" className="text-xs text-sky-300 hover:underline" data-testid="dashboard-ack-view-log">EDI log</Link>} testId="dashboard-ack-overdue">
        {d?.ack_overdue?.length ? (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr><Th>Message</Th><Th>Summary</Th><Th>Control</Th><Th>Sent</Th></tr></thead>
            <tbody className="divide-y divide-slate-800/70">
              {d.ack_overdue.map((m) => (
                <tr key={m.id} data-testid={`ack-overdue-${m.id}`}>
                  <Td className="whitespace-nowrap font-mono text-rose-300"><AlertTriangle className="mr-1 inline h-3 w-3" /><EdiTag code={m.doc_type} /></Td>
                  <Td className="text-slate-200">{m.summary}</Td>
                  <Td className="font-mono">{m.control}</Td>
                  <Td className="whitespace-nowrap font-mono text-slate-500">{ts(m.sent_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : (
          <Empty testId="dashboard-ack-empty">Every 940 / 850 sent to Group O has been acknowledged (or is within {d?.ack_overdue_hours ?? 24}h).</Empty>
        )}
      </Panel>
      <EdiImportDialog open={imp} onOpenChange={setImp} onDone={load} />
    </div>
  );
}
